import React, { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Check, X } from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "components/ui/card";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { useAuth } from "contexts/AuthContext";
import api from "lib/api";

const BTN = "w-full bg-blue-600 text-white hover:bg-blue-700";

function EmailStep({ email, setEmail, onSubmit, loading, error, oidcEnabled }) {
  return (
    <div>
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Acessar o sistema</h1>
        <p className="text-sm text-gray-500 mt-2">Entre com o e-mail cadastrado no sistema.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700">E-mail</Label>
            <Input id="email" type="email" placeholder="voce@exemplo.com" value={email}
              onChange={(e) => setEmail(e.target.value)} error={!!error} required autoFocus autoComplete="email"
              className="bg-white text-gray-900 border-gray-300 placeholder:text-gray-400" />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <Button type="submit" className={BTN} size="lg" loading={loading}>Continuar</Button>
        </form>
        {oidcEnabled && (
          <div className="mt-4">
            <div className="flex items-center gap-2 my-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-xs text-slate-400 uppercase">ou</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>
            <a href="/oidc/login"
              className="w-full inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-white border border-slate-300 px-3.5 py-2.5 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50 transition-colors duration-150">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 23 23" aria-hidden="true">
                <rect x="1" y="1" width="10" height="10" fill="#F25022" />
                <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
                <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
                <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
              </svg>
              Entrar com Office 365
            </a>
          </div>
        )}
      </CardContent>
      <CardFooter className="justify-center">
        <p className="text-xs text-center text-gray-500 leading-relaxed max-w-[280px]">
          Caso não lembre seu e-mail cadastrado entre em contato através do nosso e-mail{" "}
          <a href="mailto:contato@sec4us.com.br" className="text-blue-600 hover:underline font-medium">
            contato@sec4us.com.br
          </a>
        </p>
      </CardFooter>
    </div>
  );
}

function MfaStep({ code, setCode, onSubmit, onBack, loading, error }) {
  const inputRefs = useRef([]);
  const autoSubmitRef = useRef(false);

  useEffect(() => { inputRefs.current[0]?.focus(); }, []);
  useEffect(() => {
    if (code.every((d) => d !== "") && !autoSubmitRef.current && !loading) {
      autoSubmitRef.current = true;
      onSubmit(code.join(""));
    }
    if (code.some((d) => d === "")) autoSubmitRef.current = false;
  }, [code, onSubmit, loading]);

  const handleChange = useCallback((index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  }, [code, setCode]);

  const handleKeyDown = useCallback((index, e) => {
    if (e.key === "Backspace" && !code[index] && index > 0) inputRefs.current[index - 1]?.focus();
  }, [code]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, 6);
    if (pasted.length) {
      const newCode = [...code];
      for (let i = 0; i < pasted.length && i < 6; i++) newCode[i] = pasted[i];
      setCode(newCode);
      inputRefs.current[Math.min(pasted.length, 5)]?.focus();
    }
  }, [code, setCode]);

  return (
    <div>
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Acessar o sistema</h1>
        <p className="text-sm text-gray-500 mt-2">Digite o código de 6 dígitos do seu app autenticador.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-center gap-3" onPaste={handlePaste}>
              {[0, 1, 2, 3, 4, 5].map((index) => (
                <input key={index} ref={(el) => (inputRefs.current[index] = el)} type="text" inputMode="numeric"
                  maxLength={1} value={code[index]} onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)} autoComplete="one-time-code"
                  className={`w-12 h-14 text-center text-xl font-semibold text-gray-900 rounded-lg border transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    error ? "border-red-400 bg-red-50" : code[index] ? "border-blue-400 bg-white" : "border-gray-200 bg-gray-100"
                  }`} />
              ))}
            </div>
            {error && <p className="text-xs text-red-500 text-center mt-2">{error}</p>}
          </div>
          <Button type="submit" className={BTN} size="lg" loading={loading}>Validar código</Button>
        </form>
      </CardContent>
      <CardFooter className="flex-col items-center gap-3">
        <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">← Voltar</button>
      </CardFooter>
    </div>
  );
}

function PasswordStep({ password, setPassword, onSubmit, onBack, loading, error }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Acessar o sistema</h1>
        <p className="text-sm text-gray-500 mt-2">Informe a senha da sua conta para continuar.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-700">Senha</Label>
            <div className="relative">
              <Input id="password" type={show ? "text" : "password"} placeholder="Digite sua senha" value={password}
                onChange={(e) => setPassword(e.target.value)} error={!!error} required autoFocus autoComplete="current-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400" />
              <button type="button" onClick={() => setShow(!show)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
          </div>
          <Button type="submit" className={BTN} size="lg" loading={loading}>Entrar</Button>
        </form>
      </CardContent>
      <CardFooter className="justify-center">
        <button type="button" onClick={onBack} className="text-sm text-gray-500 hover:text-gray-900 transition-colors">← Voltar</button>
      </CardFooter>
    </div>
  );
}

function ChangePasswordStep({ tempToken, onSuccess }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);

  const checks = [
    { label: "Mínimo 8 caracteres", ok: newPassword.length >= 8 },
    { label: "Letra maiúscula", ok: /[A-Z]/.test(newPassword) },
    { label: "Letra minúscula", ok: /[a-z]/.test(newPassword) },
    { label: "Caractere especial", ok: /[^a-zA-Z0-9]/.test(newPassword) },
    { label: "Senhas coincidem", ok: newPassword && newPassword === confirmPassword },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors([]);
    if (newPassword !== confirmPassword) { setErrors(["As senhas não coincidem."]); return; }
    setLoading(true);
    try {
      const res = await api.post("/auth/change-password",
        { new_password: newPassword, confirm_password: confirmPassword },
        { headers: { Authorization: `Bearer ${tempToken}` } });
      onSuccess(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setErrors(Array.isArray(detail) ? detail : [typeof detail === "string" ? detail : "Erro ao alterar a senha."]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <CardHeader className="text-center pb-2">
        <h1 className="text-2xl font-bold tracking-tight text-gray-900">Alterar Senha</h1>
        <p className="text-sm text-gray-500 mt-2">Sua senha precisa ser alterada antes de continuar.</p>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password" className="text-gray-700">Nova Senha</Label>
            <div className="relative">
              <Input id="new-password" type={showNew ? "text" : "password"} placeholder="Digite a nova senha" value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)} required autoFocus autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400" />
              <button type="button" onClick={() => setShowNew(!showNew)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors">
                {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password" className="text-gray-700">Confirmar Nova Senha</Label>
            <div className="relative">
              <Input id="confirm-password" type={showConfirm ? "text" : "password"} placeholder="Confirme a nova senha" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} required autoComplete="new-password"
                className="pr-10 bg-white text-gray-900 border-gray-300 placeholder:text-gray-400" />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} tabIndex={-1}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-900 transition-colors">
                {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {newPassword && (
            <div className="space-y-1.5 pt-1">
              {checks.map((c, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  {c.ok ? <Check className="h-3.5 w-3.5 text-emerald-500" /> : <X className="h-3.5 w-3.5 text-gray-300" />}
                  <span className={c.ok ? "text-emerald-500" : "text-gray-400"}>{c.label}</span>
                </div>
              ))}
            </div>
          )}
          {errors.length > 0 && <div className="space-y-1 pt-1">{errors.map((e, i) => <p key={i} className="text-xs text-red-500">{e}</p>)}</div>}
          <Button type="submit" className={BTN} size="lg" loading={loading}>Alterar Senha</Button>
        </form>
      </CardContent>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { user, loginUser } = useAuth();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [mfaCode, setMfaCode] = useState(["", "", "", "", "", ""]);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [oidcEnabled, setOidcEnabled] = useState(false);

  useEffect(() => { if (user) navigate("/", { replace: true }); }, [user, navigate]);
  useEffect(() => {
    api.get("/auth/config").then((r) => setOidcEnabled(!!r.data?.oidc_enabled)).catch(() => {});
  }, []);

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.post("/auth/email", { email });
      if (res.data?.skip_mfa) { setLoading(false); setStep(3); return; }
    } catch { /* segue para etapa 2 */ }
    finally { setLoading(false); }
    if (step === 1) setStep(2);
  };

  const handleMfaSubmit = async (codeOrEvent) => {
    let codeString;
    if (typeof codeOrEvent === "string") codeString = codeOrEvent;
    else { codeOrEvent.preventDefault(); codeString = mfaCode.join(""); }
    if (codeString.length !== 6) { setError("Digite o código completo de 6 dígitos."); return; }
    setError(""); setLoading(true);
    try {
      await api.post("/auth/mfa", { code: codeString });
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || "Código inválido. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const res = await api.post("/auth/password", { password });
      if (res.data?.must_change_password) {
        setTempToken(res.data.token);
        setMustChangePassword(true);
        setLoading(false);
        return;
      }
      if (res.data?.user && res.data?.token) await loginUser(res.data.user, res.data.token);
      navigate("/");
    } catch (err) {
      setError(err.response?.data?.detail || "Senha incorreta. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChanged = async (data) => {
    if (data?.user && data?.token) await loginUser(data.user, data.token);
    navigate("/");
  };

  const goToStep = (target) => {
    setError("");
    if (target < step) {
      if (target <= 2) setPassword("");
      if (target <= 1) setMfaCode(["", "", "", "", "", ""]);
    }
    setStep(target);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f5f5f7] px-4 py-8">
      <div className="w-full max-w-[420px]">
        <Card className="shadow-lg border-0 bg-white rounded-2xl">
          {mustChangePassword ? (
            <ChangePasswordStep tempToken={tempToken} onSuccess={handlePasswordChanged} />
          ) : (
            <>
              {step === 1 && <EmailStep email={email} setEmail={setEmail} onSubmit={handleEmailSubmit} loading={loading} error={error} oidcEnabled={oidcEnabled} />}
              {step === 2 && <MfaStep code={mfaCode} setCode={setMfaCode} onSubmit={handleMfaSubmit} onBack={() => goToStep(1)} loading={loading} error={error} />}
              {step === 3 && <PasswordStep password={password} setPassword={setPassword} onSubmit={handlePasswordSubmit} onBack={() => goToStep(2)} loading={loading} error={error} />}
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
